import { CodeLang } from '@common/codemirror/types/code-lang.type';
import { ConditionSetSchema } from '@common/flare-data/schemas/condition-set-schema';
import { CssSchema } from '@common/flare-data/schemas/css-schema';
import { DestinationSchema } from '@common/flare-data/schemas/destination-schema';
import { ProjectSchema } from '@common/flare-data/schemas/project-schema';
import { TargetSchema } from '@common/flare-data/schemas/target-schema';
import { TocSchema } from '@common/flare-data/schemas/toc-schema';
import { VariableSetSchema } from '@common/flare-data/schemas/variable-set-schema';
import { FlareSchema } from '@common/flare/flare-schema';
import { ProjectFileType } from '@common/project-files/enums/project-file-type.enum';
import { getFileType } from '@common/project-files/util/files';
import { CollabSchema } from '@common/prosemirror/model/collab-schema';

const flare = new FlareSchema();
const variableSet = new VariableSetSchema();
const conditionSet = new ConditionSetSchema();
const target = new TargetSchema();
const toc = new TocSchema();
const css = new CssSchema();
const destination = new DestinationSchema();
const project = new ProjectSchema();

export const FlareSchemaName = 'flare';

export function getSchemaByName(schemaName: string): CollabSchema | undefined {
  switch (schemaName) {
    case flare.name: return flare;
    case variableSet.name: return variableSet;
    case conditionSet.name: return conditionSet;
    case target.name: return target;
    case toc.name: return toc;
    case css.name: return css;
    case destination.name: return destination;
    case project.name: return project;
  }
}

export function getSchemaByFileType(fileType: ProjectFileType): CollabSchema | undefined {
  switch (fileType) {
    case ProjectFileType.Flare:
      return flare;
    case ProjectFileType.ConditionTagSet:
      return conditionSet;
    case ProjectFileType.VariableSet:
      return variableSet;
    case ProjectFileType.Target:
      return target;
    case ProjectFileType.Toc:
      return toc;
    case ProjectFileType.Stylesheet:
      return css;
    case ProjectFileType.Destination:
      return destination;
    case ProjectFileType.Project:
      return project;
  }
}

export function getSchemaByFilePath(filePath: string): CollabSchema | undefined {
  const fileType = getFileType(filePath);
  return getSchemaByFileType(fileType);
}

export function getSchemaByParams(reviewFileId?: number, filePath?: string): CollabSchema | undefined {
  if (Number.isInteger(reviewFileId)) {
    return getSchemaByName(FlareSchemaName); // only Topic files can be reviewed
  }
  if (filePath) {
    return getSchemaByFilePath(filePath);
  }
}

/** Gets the project file type by the prosemirror document schema name. */
export function getProjectFileTypeBySchemaName(schemaName: string): ProjectFileType {
  switch (schemaName) {
    case flare.name:
      return ProjectFileType.Flare;
    case css.name:
      return ProjectFileType.Stylesheet;
    case conditionSet.name:
      return ProjectFileType.ConditionTagSet;
    case target.name:
      return ProjectFileType.Target;
    case toc.name:
      return ProjectFileType.Toc;
    case variableSet.name:
      return ProjectFileType.VariableSet;
    case destination.name:
      return ProjectFileType.Destination;
    case project.name:
      return ProjectFileType.Project;
    default:
      return undefined;
  }
}

/** Gets the code language by the prosemirror document schema name. */
export function getCodeLangBySchemaName(schemaName: string): CodeLang {
  switch (schemaName) {
    case flare.name:
      return 'flare';
    case css.name:
      return 'css';
    case conditionSet.name:
    case target.name:
    case toc.name:
    case variableSet.name:
    case destination.name:
    case project.name:
      return 'xml';
    default:
      return undefined;
  }
}
